import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { api } from "@/lib/api";
import { useSectionBackground } from "@/lib/sectionBackground";
import { LogOut, User, Trophy, BookOpen, Newspaper, Users, ClipboardList, Shield, Layers, Menu, X, Sparkles } from "lucide-react";
import BottomNav from "@/components/BottomNav";
import NotificationsBell from "@/components/NotificationsBell";
import SocialIcons from "@/components/SocialIcons";
import TimezonePicker from "@/components/TimezonePicker";
import LanguagePicker from "@/components/LanguagePicker";
import { useI18n } from "@/lib/I18nContext";

const navItems = [
  { to: "/perfil", labelKey: "nav.profile", icon: User, testid: "nav-perfil" },
  { to: "/reglas", labelKey: "nav.rules", icon: BookOpen, testid: "nav-reglas" },
  { to: "/pronosticos/fase1", labelKey: "nav.phase1", icon: ClipboardList, testid: "nav-fase1" },
  { to: "/pronosticos/fase2", labelKey: "nav.phase2", icon: Layers, testid: "nav-fase2" },
  { to: "/ranking", labelKey: "nav.ranking", icon: Trophy, testid: "nav-ranking" },
  { to: "/todos", labelKey: "nav.realScores", icon: Users, testid: "nav-todos" },
  { to: "/pollas-especiales", labelKey: "nav.specialPools", icon: Sparkles, testid: "nav-pollas-especiales", optional: true },
  { to: "/noticias", labelKey: "nav.news", icon: Newspaper, testid: "nav-noticias" },
];

const adminNavItem = { to: "/admin", labelKey: "nav.admin", icon: Shield, testid: "nav-admin" };

const SPECIAL_ONLY_PATHS = new Set(["/pollas-especiales", "/noticias"]);

const ColorBar = () => (
  <div className="color-bar">
    {["red","orange","yellow","lime","green","mint","sky","blue","mauve","purple","pink","peach"].map((c) => (
      <div key={c} style={{ background: `var(--c-${c})` }} />
    ))}
  </div>
);

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const [hasSpecialPools, setHasSpecialPools] = useState(false);
  const { imageUrl: bgImage } = useSectionBackground();

  useEffect(() => {
    api.get("/special-pools")
      .then((r) => setHasSpecialPools((r.data || []).length > 0))
      .catch(() => setHasSpecialPools(false));
  }, []);

  const isSpecialOnly = !!user?.is_special_only && !user?.is_admin;
  const baseItems = isSpecialOnly
    ? navItems.filter((n) => SPECIAL_ONLY_PATHS.has(n.to))
    : navItems.filter((n) => !n.optional || hasSpecialPools);
  const items = user?.is_admin ? [...baseItems, adminNavItem] : baseItems;

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  // Close menu when route changes
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  // Lock body scroll when menu is open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  return (
    <div className="grain min-h-screen bg-[#0A0A0A] text-white relative">
      {bgImage && (
        <div
          aria-hidden="true"
          data-testid="section-bg-image"
          className="fixed inset-0 z-0 bg-cover bg-center bg-no-repeat pointer-events-none"
          style={{ backgroundImage: `url("${bgImage}")` }}
        />
      )}
      {bgImage && (
        <div
          aria-hidden="true"
          data-testid="section-bg-overlay"
          className="fixed inset-0 z-0 pointer-events-none"
          style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.88) 60%, rgba(0,0,0,0.94) 100%)" }}
        />
      )}
      <ColorBar />
      <header className="glass sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 md:h-24 flex items-center justify-between gap-4">
          <Link to="/perfil" className="flex items-center gap-3" data-testid="brand-link">
            <img src="/img/trophy.png" alt="La Polla Mundialista" className="w-14 h-14 md:w-16 md:h-16 object-contain" />
            <div className="leading-none">
              <div className="font-display text-2xl md:text-3xl uppercase tracking-tight">{t("nav.brand")}</div>
              <div className="text-[11px] md:text-xs text-zinc-400 uppercase tracking-widest font-mono-data">{t("nav.brandSub")}</div>
            </div>
          </Link>
          <div className="flex items-center gap-2 md:gap-3">
            <div className="text-right hidden sm:block">
              <div className="text-sm font-semibold" data-testid="user-nickname">{user?.nickname}</div>
              <div className="text-[10px] uppercase text-zinc-500 tracking-wider">{user?.is_admin ? t("common.admin") : t("common.player")}</div>
            </div>
            <TimezonePicker />
            <LanguagePicker />
            <NotificationsBell />
            <button
              onClick={() => setMenuOpen(true)}
              data-testid="hamburger-button"
              aria-label={t("common.menu")}
              className="px-3 py-3 md:px-4 md:py-3 border border-zinc-800 hover:border-[var(--c-lime)] transition-colors flex items-center gap-2"
            >
              <Menu size={22} />
              <span className="hidden md:inline text-sm uppercase tracking-widest font-mono-data">{t("common.menu")}</span>
            </button>
          </div>
        </div>
      </header>

      {/* Slide-in menu drawer */}
      {menuOpen && (
        <>
          <div
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm"
            onClick={() => setMenuOpen(false)}
            data-testid="menu-backdrop"
          />
          <aside
            className="fixed top-0 right-0 z-50 h-full w-full sm:w-96 bg-[#0A0A0A] border-l border-zinc-800 shadow-2xl overflow-y-auto slide-up"
            data-testid="menu-drawer"
          >
            <div className="px-6 py-5 border-b border-zinc-800 flex items-center justify-between">
              <div>
                <div className="font-display text-xl uppercase">{t("common.menu")}</div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono-data mt-1">{user?.nickname}</div>
              </div>
              <button onClick={() => setMenuOpen(false)} data-testid="menu-close-button" aria-label={t("common.close")}
                className="p-2 border border-zinc-800 hover:border-white">
                <X size={18} />
              </button>
            </div>
            <nav className="px-4 py-4 space-y-1">
              {items.map(({ to, labelKey, icon: Icon, testid }) => (
                <NavLink
                  key={to}
                  to={to}
                  data-testid={`${testid}-menu`}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-3 text-sm uppercase tracking-wider transition-colors ${
                      isActive
                        ? "bg-[var(--c-lime)] text-black font-bold"
                        : "text-zinc-300 hover:bg-zinc-900 border border-transparent hover:border-zinc-800"
                    }`
                  }
                >
                  <Icon size={16} /> <span className="flex-1">{t(labelKey)}</span>
                </NavLink>
              ))}
            </nav>
            <div className="px-4 py-4 border-t border-zinc-800">
              <button
                onClick={async () => { setMenuOpen(false); await logout(); navigate("/login"); }}
                data-testid="logout-button"
                className="w-full flex items-center gap-3 px-3 py-3 text-sm uppercase tracking-wider text-[var(--c-red)] border border-zinc-800 hover:border-[var(--c-red)]"
              >
                <LogOut size={16} /> {t("common.signout")}
              </button>
            </div>
          </aside>
        </>
      )}

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-28 relative z-10">{children}</main>

      <footer className="border-t border-zinc-900 mt-16 pb-24 relative z-10">
        <div className="max-w-7xl mx-auto px-4 py-10 space-y-8">
          <div className="text-center">
            <div className="text-sm uppercase tracking-widest text-white font-mono-data mb-3">{t("footer.follow")}</div>
            <div className="font-display text-2xl md:text-3xl uppercase mb-5">{t("footer.tagline")}</div>
            <SocialIcons settings={settings} testidPrefix="social" />
          </div>
          <div className="flex items-center justify-between text-xs uppercase tracking-wider text-zinc-500 flex-wrap gap-2 border-t border-zinc-900 pt-6">
            <span>{t("footer.copyright")}</span>
          </div>
        </div>
      </footer>

      <BottomNav />
    </div>
  );
}
